/**
 * Ordinal Pass session in the browser.
 *
 * The access token lives in memory only (this module) — never in
 * localStorage or sessionStorage. The refresh token never reaches JavaScript:
 * this app's backend (/api/auth/*, api/_lib/trust.ts) keeps it in an HttpOnly,
 * SameSite=Strict cookie scoped to /api/auth. A reload calls /api/auth/refresh
 * to get a new access token from that cookie.
 */

export interface PassSession {
  accessToken: string;
  /** epoch ms */
  expiresAt: number;
  /** Trust subject (today: the Trust user id; Ordinal Pass id once Trust maps it, design G4). */
  sub: string;
  /** The one workspace this session is scoped to (always set by the backend). */
  workspaceId?: string;
  workspaceSlug?: string;
  /** Ordinal Pass id, once Trust emits it (design T-3, PM-4 A). */
  ordinalPassId?: string;
  /** Entity (company) id, optional, picked after login (design T-4, PM-4 A). */
  entityId?: string;
  assuranceLevel?: string;
}

export interface SessionResponse {
  access_token: string;
  expires_in: number;
  profile?: {
    sub?: string;
    workspace_id?: string;
    workspace_slug?: string;
    ordinal_pass_id?: string;
    entity_id?: string;
    assurance_level?: string;
  };
}

export class SessionError extends Error {
  code: string;
  status: number;
  constructor(code: string, message: string, status = 0) {
    super(message);
    this.name = 'SessionError';
    this.code = code;
    this.status = status;
  }
}

let current: PassSession | null = null;
const listeners = new Set<(s: PassSession | null) => void>();

export function getSession(): PassSession | null {
  if (current && current.expiresAt <= Date.now()) return null;
  return current;
}

export function setSession(s: PassSession | null): void {
  current = s;
  for (const l of listeners) l(s);
}

export function onSessionChange(l: (s: PassSession | null) => void): () => void {
  listeners.add(l);
  return () => listeners.delete(l);
}

/** Pure: backend answer → session (null when the answer is unusable). */
export function sessionFromResponse(r: unknown, now: number = Date.now()): PassSession | null {
  if (!r || typeof r !== 'object') return null;
  const b = r as SessionResponse;
  if (typeof b.access_token !== 'string' || !b.access_token) return null;
  const ttl = Number(b.expires_in);
  if (!Number.isFinite(ttl) || ttl <= 0) return null;
  const p = b.profile || {};
  if (typeof p.sub !== 'string' || !p.sub) return null;
  const opt = (v: unknown) => (typeof v === 'string' && v ? v : undefined);
  const s: PassSession = { accessToken: b.access_token, expiresAt: now + ttl * 1000, sub: p.sub };
  const ws = opt(p.workspace_id); if (ws) s.workspaceId = ws;
  const slug = opt(p.workspace_slug); if (slug) s.workspaceSlug = slug;
  const pass = opt(p.ordinal_pass_id); if (pass) s.ordinalPassId = pass;
  const ent = opt(p.entity_id); if (ent) s.entityId = ent;
  const al = opt(p.assurance_level); if (al) s.assuranceLevel = al;
  return s;
}

async function post(path: string, body?: unknown): Promise<unknown> {
  let res: Response;
  try {
    res = await fetch(path, {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', 'X-Collect-Auth': '1' },
      body: body === undefined ? undefined : JSON.stringify(body),
    });
  } catch {
    throw new SessionError('NETWORK', 'Could not reach the Collect sign-in service.');
  }
  let data: any = null;
  try { data = await res.json(); } catch { /* not JSON */ }
  if (!res.ok) {
    throw new SessionError(data?.error || `HTTP_${res.status}`, data?.message || `Sign-in service answered ${res.status}.`, res.status);
  }
  return data;
}

/** /auth/callback: code + PKCE verifier + nonce → session (refresh cookie set by the backend). */
export async function exchangeCode(input: { code: string; verifier: string; redirectUri: string; nonce: string }): Promise<PassSession> {
  const data = await post('/api/auth/token', {
    code: input.code,
    code_verifier: input.verifier,
    redirect_uri: input.redirectUri,
    nonce: input.nonce,
  });
  const s = sessionFromResponse(data);
  if (!s) throw new SessionError('BAD_RESPONSE', 'The sign-in service returned no usable session.');
  setSession(s);
  return s;
}

let restoring: Promise<PassSession | null> | null = null;

/**
 * Page load / expiry: new access token from the refresh cookie, or null when
 * there is none. Single-flight: Trust rotates refresh tokens, so two parallel
 * refreshes with one cookie would make the second fail. A `session_expired`
 * answer is retried once after a short pause — another tab may have just
 * rotated the cookie.
 */
export function restoreSession(): Promise<PassSession | null> {
  if (!restoring) {
    restoring = refreshWithRetry().finally(() => { restoring = null; });
  }
  return restoring;
}

async function refreshWithRetry(): Promise<PassSession | null> {
  for (let attempt = 0; ; attempt++) {
    try {
      const s = sessionFromResponse(await post('/api/auth/refresh'));
      setSession(s);
      return s;
    } catch (e) {
      if (e instanceof SessionError && e.code === 'session_expired' && attempt === 0) {
        await new Promise((r) => setTimeout(r, 700));
        continue;
      }
      if (e instanceof SessionError && (e.status === 401 || e.status === 404 || e.status === 503)) {
        setSession(null);
        return null;
      }
      throw e;
    }
  }
}

export async function signOut(): Promise<void> {
  setSession(null);
  try {
    await post('/api/auth/logout');
  } catch {
    /* the cookie expires on its own; nothing else to do */
  }
}
